import type { TaskTrackerBoardFilterStickerRow } from './hooks/useTaskTrackerBoardFilterStickers'
import {
  buildRuleColumnsConfig,
  resolveRulePrimaryBoardId,
  type BoardSummaryRuleDraft,
} from './taskTrackerBoardSummaryRules.utils'
import {
  mapDraftRowsToSortingConfig,
  type BoardSummarySortingDraftRow,
} from './taskTrackerBoardSummarySorting.utils'
import {
  isTaskTrackerStickerFilterDraftEmpty,
  isTaskTrackerDateLikeStickerFieldType,
  isTaskTrackerStickerFilterRangeDraft,
  normalizeTaskTrackerStickerFilterRangeValue,
  normalizeTaskTrackerStickerFilterValue,
  normalizeTaskTrackerStickerFilterValues,
  type TaskTrackerStickerFilterDraftValue,
} from './taskTrackerBoard.stickerFilters.utils'
import type {
  BoardSummaryFilterDraftRow,
  BoardSummaryFilterDraftValue,
} from './taskTrackerBoardSummaryStickerFilters.utils'
import {
  createBoardSummaryStickerFilterRowId,
  isBoardSummaryStatusFilterValue,
} from './taskTrackerBoardSummaryStickerFilters.utils'
import {
  isTaskTrackerSummaryDeadlineFilterDraftEmpty,
  normalizeSummaryDeadlineFilterDraft,
} from './taskTrackerBoardSummaryDeadlineFilter.utils'
import type {
  TaskTrackerBoardSummary,
  TaskTrackerBoardSummarySavePayload,
  TaskTrackerSummaryColumnsProjectConfig,
  TaskTrackerSummaryFilterGroup,
  TaskTrackerSummaryFilterLeaf,
  TaskTrackerSummaryStickerFilter,
} from './taskTrackerBoardSummary.types'

const isSummaryFilterGroup = (
  value: TaskTrackerSummaryFilterLeaf | TaskTrackerSummaryFilterGroup,
): value is TaskTrackerSummaryFilterGroup =>
  value != null &&
  typeof value === 'object' &&
  Array.isArray((value as TaskTrackerSummaryFilterGroup).items)

export const extractPrimarySummarySelection = (
  columnsConfig: TaskTrackerSummaryColumnsProjectConfig[],
) => {
  const project = columnsConfig[0]
  const board = project?.boards?.[0]

  return {
    projectId: project?.projectId ?? null,
    boardId: board?.boardId ?? null,
    columnIds: [...new Set(board?.columnIds ?? [])],
  }
}

export const buildSummaryColumnsConfig = (
  projectId: number,
  boardId: number,
  columnIds: number[],
): TaskTrackerSummaryColumnsProjectConfig[] => [
  {
    projectId,
    boards: [
      {
        boardId,
        columnIds: [...new Set(columnIds.filter((columnId) => columnId > 0))],
      },
    ],
  },
]

export const collectSummaryFilterLeaves = (
  groups: TaskTrackerSummaryFilterGroup[],
): TaskTrackerSummaryFilterLeaf[] => {
  const leaves: TaskTrackerSummaryFilterLeaf[] = []

  groups.forEach((group) => {
    ;(group.items ?? []).forEach((item) => {
      if (isSummaryFilterGroup(item)) {
        leaves.push(...collectSummaryFilterLeaves([item]))
        return
      }

      leaves.push(item)
    })
  })

  return leaves
}

const mapSummaryStickerApiValuesToDraftValue = (
  sticker: TaskTrackerBoardFilterStickerRow,
  values: string[],
): TaskTrackerStickerFilterDraftValue => {
  if (sticker.fieldType === 'select') {
    return values
  }

  return values[0] ?? null
}

const mapSummaryStickerApiValueToDraftValue = (
  sticker: TaskTrackerBoardFilterStickerRow,
  value: string | null | undefined,
): TaskTrackerStickerFilterDraftValue => {
  if (isTaskTrackerDateLikeStickerFieldType(sticker.fieldType)) {
    const normalized = normalizeTaskTrackerStickerFilterRangeValue(value)
    return normalized ? { from: normalized, to: normalized } : null
  }

  const normalized = normalizeTaskTrackerStickerFilterValue(value)

  return normalized
}

const mapSummaryStickerDraftValueToApiValues = (
  sticker: TaskTrackerBoardFilterStickerRow,
  value: BoardSummaryFilterDraftValue,
): string[] => {
  if (isBoardSummaryStatusFilterValue(value)) return []

  if (sticker.fieldType === 'performer') {
    return (Array.isArray(value) ? value : [])
      .map((item) => String(item).trim())
      .filter(Boolean)
  }

  if (sticker.fieldType === 'select') {
    return normalizeTaskTrackerStickerFilterValues(
      Array.isArray(value) ? value : [],
    )
  }

  if (sticker.fieldType === 'text') {
    const normalized = normalizeTaskTrackerStickerFilterValue(
      typeof value === 'string' ? value : null,
    )
    return normalized ? [normalized] : []
  }

  if (
    sticker.fieldType === 'number' ||
    isTaskTrackerDateLikeStickerFieldType(sticker.fieldType)
  ) {
    const normalized = normalizeTaskTrackerStickerFilterValue(
      typeof value === 'string' ? value : null,
    )
    return normalized ? [normalized] : []
  }

  return []
}

const isSummaryFilterRowValueEmpty = (
  row: BoardSummaryFilterDraftRow,
  sticker: TaskTrackerBoardFilterStickerRow | null,
) => {
  if (row.filterType == null) return true

  if (row.filterType === 'status') {
    return !isBoardSummaryStatusFilterValue(row.value)
  }

  if (!sticker) return true

  if (sticker.fieldType === 'deadline') {
    return isTaskTrackerSummaryDeadlineFilterDraftEmpty(
      row.value as TaskTrackerStickerFilterDraftValue,
    )
  }

  if (sticker.fieldType === 'performer') {
    const values = (Array.isArray(row.value) ? row.value : [])
      .map((value) => Number(value))
      .filter((value) => Number.isFinite(value) && value > 0)
    return values.length === 0
  }

  return isTaskTrackerStickerFilterDraftEmpty(
    row.value as TaskTrackerStickerFilterDraftValue,
  )
}

export const mapSummaryFilterRowsToFiltersConfig = (
  rows: BoardSummaryFilterDraftRow[],
  stickerRows: TaskTrackerBoardFilterStickerRow[],
): TaskTrackerSummaryFilterGroup[] => {
  const items: TaskTrackerSummaryFilterLeaf[] = []

  rows.forEach((row) => {
    if (row.filterType == null) return

    if (row.filterType === 'status') {
      if (!isBoardSummaryStatusFilterValue(row.value)) return
      items.push({ field: 'status', value: row.value })
      return
    }

    const sticker =
      stickerRows.find((item) => item.fieldSettingId === row.filterType) ?? null
    if (!sticker || isSummaryFilterRowValueEmpty(row, sticker)) return

    if (sticker.fieldType === 'performer') {
      const values = (Array.isArray(row.value) ? row.value : [])
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)

      if (values.length > 0) {
        items.push({ field: 'performer', values })
      }
      return
    }

    if (sticker.fieldType === 'deadline') {
      const deadlineDraft = normalizeSummaryDeadlineFilterDraft(
        row.value as TaskTrackerStickerFilterDraftValue,
      )
      if (!deadlineDraft) return

      if (deadlineDraft.operator === 'range') {
        const from = deadlineDraft.from?.trim() || null
        const to = deadlineDraft.to?.trim() || null
        if (!from && !to) return

        items.push({
          field: 'deadline',
          operator: 'range',
          from,
          to,
        })
        return
      }

      if (deadlineDraft.operator === 'within_days') {
        const days = Number(deadlineDraft.days)
        if (!Number.isFinite(days) || days <= 0) return

        items.push({
          field: 'deadline',
          operator: 'within_days',
          days: Math.trunc(days),
        })
        return
      }

      items.push({
        field: 'deadline',
        operator: deadlineDraft.operator,
      })
      return
    }

    if (
      isTaskTrackerDateLikeStickerFieldType(sticker.fieldType) &&
      isTaskTrackerStickerFilterRangeDraft(
        row.value as TaskTrackerStickerFilterDraftValue,
      )
    ) {
      const range = row.value as TaskTrackerStickerFilterDraftValue
      if (!isTaskTrackerStickerFilterRangeDraft(range)) return

      const from = normalizeTaskTrackerStickerFilterRangeValue(range.from)
      const to = normalizeTaskTrackerStickerFilterRangeValue(range.to)
      if (!from && !to) return

      items.push({
        field: 'sticker',
        fieldSettingId: sticker.fieldSettingId,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
      })
      return
    }

    const values = mapSummaryStickerDraftValueToApiValues(sticker, row.value)
    if (values.length === 0) return

    if (isTaskTrackerDateLikeStickerFieldType(sticker.fieldType)) {
      items.push({
        field: 'sticker',
        fieldSettingId: sticker.fieldSettingId,
        value: values[0],
      })
      return
    }

    items.push({
      field: 'sticker',
      fieldSettingId: sticker.fieldSettingId,
      values,
    })
  })

  if (items.length === 0) return []

  return [{ logic: 'and', items }]
}

export const mapSummaryFiltersConfigToFilterRows = (
  filtersConfig: TaskTrackerSummaryFilterGroup[],
  stickerRows: TaskTrackerBoardFilterStickerRow[],
): BoardSummaryFilterDraftRow[] => {
  const performerSticker = stickerRows.find(
    (sticker) => sticker.fieldType === 'performer',
  )
  const deadlineSticker = stickerRows.find(
    (sticker) => sticker.fieldType === 'deadline',
  )
  const rows: BoardSummaryFilterDraftRow[] = []

  collectSummaryFilterLeaves(filtersConfig).forEach((leaf) => {
    if (leaf.field === 'status') {
      rows.push({
        id: createBoardSummaryStickerFilterRowId(),
        filterType: 'status',
        value: leaf.value,
      })
      return
    }

    if (leaf.field === 'performer' && performerSticker) {
      const values = (leaf.values ?? [])
        .map((value) => String(value))
        .filter(Boolean)

      if (values.length === 0) return

      rows.push({
        id: createBoardSummaryStickerFilterRowId(),
        filterType: performerSticker.fieldSettingId,
        value: values,
      })
      return
    }

    if (leaf.field === 'deadline' && deadlineSticker) {
      if (leaf.operator === 'range') {
        rows.push({
          id: createBoardSummaryStickerFilterRowId(),
          filterType: deadlineSticker.fieldSettingId,
          value: {
            operator: 'range',
            from: leaf.from?.trim() || null,
            to: leaf.to?.trim() || null,
          },
        })
        return
      }

      if (leaf.operator === 'within_days') {
        const days = Number(leaf.days)
        if (!Number.isFinite(days) || days <= 0) return

        rows.push({
          id: createBoardSummaryStickerFilterRowId(),
          filterType: deadlineSticker.fieldSettingId,
          value: {
            operator: 'within_days',
            days: Math.trunc(days),
          },
        })
        return
      }

      rows.push({
        id: createBoardSummaryStickerFilterRowId(),
        filterType: deadlineSticker.fieldSettingId,
        value: {
          operator: leaf.operator,
        },
      })
      return
    }

    if (leaf.field === 'sticker') {
      const sticker =
        stickerRows.find(
          (item) => item.fieldSettingId === leaf.fieldSettingId,
        ) ?? null
      const value = normalizeTaskTrackerStickerFilterValue(leaf.value)
      const values = (leaf.values ?? [])
        .map((value) => String(value).trim())
        .filter(Boolean)

      if (!sticker) return

      const from = normalizeTaskTrackerStickerFilterRangeValue(leaf.from)
      const to = normalizeTaskTrackerStickerFilterRangeValue(leaf.to)
      if (from != null || to != null) {
        rows.push({
          id: createBoardSummaryStickerFilterRowId(),
          filterType: sticker.fieldSettingId,
          value: { from, to },
        })
        return
      }

      if (value != null) {
        rows.push({
          id: createBoardSummaryStickerFilterRowId(),
          filterType: sticker.fieldSettingId,
          value: mapSummaryStickerApiValueToDraftValue(sticker, value),
        })
        return
      }

      if (values.length === 0) return

      rows.push({
        id: createBoardSummaryStickerFilterRowId(),
        filterType: sticker.fieldSettingId,
        value: mapSummaryStickerApiValuesToDraftValue(sticker, values),
      })
    }
  })

  return rows
}

export const summaryFiltersConfigNeedsStickerCatalog = (
  filtersConfig: TaskTrackerSummaryFilterGroup[],
) =>
  collectSummaryFilterLeaves(filtersConfig).some(
    (leaf) =>
      leaf.field === 'sticker' ||
      leaf.field === 'performer' ||
      leaf.field === 'deadline',
  )

export const isSummaryFiltersConfigHydrationReady = (
  filtersConfig: TaskTrackerSummaryFilterGroup[],
  stickerRows: TaskTrackerBoardFilterStickerRow[],
  isLoading: boolean,
) => {
  const needsStickerCatalog = summaryFiltersConfigNeedsStickerCatalog(filtersConfig)
  if (needsStickerCatalog && isLoading) return false

  const leaves = collectSummaryFilterLeaves(filtersConfig)
  if (leaves.length === 0) return true

  return leaves.every((leaf) => {
    if (leaf.field === 'status') return true

    if (leaf.field === 'performer') {
      return stickerRows.some((sticker) => sticker.fieldType === 'performer')
    }

    if (leaf.field === 'deadline') {
      return stickerRows.some((sticker) => sticker.fieldType === 'deadline')
    }

    if (leaf.field === 'sticker') {
      return stickerRows.some(
        (sticker) => sticker.fieldSettingId === leaf.fieldSettingId,
      )
    }

    return true
  })
}

/** @deprecated use mapSummaryFilterRowsToFiltersConfig */
export const mapSummaryStickerRowsToFiltersConfig = mapSummaryFilterRowsToFiltersConfig

/** @deprecated use mapSummaryFiltersConfigToFilterRows */
export const mapSummaryFiltersConfigToStickerRows = mapSummaryFiltersConfigToFilterRows

/** @deprecated filters_config supports all sticker types */
export const getUnsupportedSummaryStickerFilterTitles = (
  _rows: BoardSummaryFilterDraftRow[],
  _stickerRows: TaskTrackerBoardFilterStickerRow[],
) => [] as string[]

export const buildBoardSummarySavePayload = ({
  title,
  rules,
  sortingRows,
  getStickerRows,
  defaultBoardId = null,
}: {
  title: string
  rules: BoardSummaryRuleDraft[]
  sortingRows: BoardSummarySortingDraftRow[]
  getStickerRows: (boardId: number | null) => TaskTrackerBoardFilterStickerRow[]
  defaultBoardId?: number | null
}): TaskTrackerBoardSummarySavePayload => ({
  title: title.trim(),
  rulesConfig: rules
    .map((rule) => {
      const columnsConfig = buildRuleColumnsConfig(rule.sources)
      if (columnsConfig.length === 0) return null

      const primaryBoardId = resolveRulePrimaryBoardId(rule, defaultBoardId)

      return {
        columnsConfig,
        filtersConfig: mapSummaryFilterRowsToFiltersConfig(
          rule.filterRows,
          getStickerRows(primaryBoardId),
        ),
      }
    })
    .filter((rule): rule is NonNullable<typeof rule> => rule != null),
  sortingConfig: mapDraftRowsToSortingConfig(sortingRows),
})

export const attachPrimarySummarySelection = (
  summary: Omit<
    TaskTrackerBoardSummary,
    'projectId' | 'boardId' | 'columnIds'
  >,
): TaskTrackerBoardSummary => {
  const selection = extractPrimarySummarySelection(summary.columnsConfig)

  return {
    ...summary,
    projectId: selection.projectId ?? 0,
    boardId: selection.boardId ?? 0,
    columnIds: selection.columnIds,
  }
}

export type { TaskTrackerSummaryStickerFilter }
